import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  buildStatusSmsText,
  getSmsTemplates,
  sendSmsToLead,
  type SendSmsResult,
} from '@/api/sms'
import { formatPhoneDisplay, formatPhoneInput } from '@/utils/phone'

interface Props {
  /** Лид, по которому отправляется SMS. Уйдёт в sms_outbox.lead_id для
   *  раздела «Коммуникации» в карточке. */
  leadId?: string
  /** Телефон клиента из лида. Может быть null — тогда диспетчер вводит руками. */
  clientPhone: string | null
  /** landing_token лида — формирует ссылку `https://avs.carlink.ru/s/<token>`
   *  на персональную статус-страницу клиента. Пустая строка / null — кнопка
   *  скрывается родителем (бессмысленно слать SMS без токена). */
  landingToken: string
  /** Опциональный хук — выполняется ДО открытия модалки. Карточка лида
   *  сюда передаёт auto-save грязного draft'а. */
  beforeOpen?: () => void | Promise<void>
  /** Внешняя блокировка кнопки (напр. лид ещё не взят в работу). */
  disabled?: boolean
}

/**
 * Кнопка «Отправить статус» — шлёт клиенту SMS со ссылкой на статус-страницу
 * заказа (`https://avs.carlink.ru/s/<landing_token>`). Страница
 * `landing-get-status` по этому токену показывает реальный этап заказа в
 * реальном времени.
 *
 * Паттерн совпадает с `SendSmsToLeadButton` (тот же `sendSmsToLead`, та же
 * модалка с превью текста и счётчиком сегментов).
 */
export function SendStatusSmsButton({ leadId, clientPhone, landingToken, beforeOpen, disabled }: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [opening, setOpening] = useState(false)
  const handleClick = async () => {
    if (beforeOpen) {
      setOpening(true)
      try {
        await beforeOpen()
      } finally {
        setOpening(false)
      }
    }
    setOpen(true)
  }
  return (
    <>
      <Button variant="secondary" onClick={handleClick} disabled={opening || disabled}>
        {opening ? t('leads.sms.saving') : t('leads.sms.statusSend')}
      </Button>
      {open ? (
        <SendStatusDialog
          leadId={leadId}
          clientPhone={clientPhone}
          landingToken={landingToken}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  )
}

function SendStatusDialog({
  leadId,
  clientPhone,
  landingToken,
  onClose,
}: {
  leadId?: string
  clientPhone: string | null
  landingToken: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [phone, setPhone] = useState(clientPhone ? formatPhoneDisplay(clientPhone) : '')
  const [result, setResult] = useState<SendSmsResult | null>(null)

  // Шаблон — из system_config (единый источник с триггерами БД).
  const { data: templates } = useQuery({
    queryKey: ['sms-templates'],
    queryFn: getSmsTemplates,
    staleTime: 60_000,
  })
  const message = buildStatusSmsText(landingToken, templates?.status)
  const phoneTrim = phone.trim()
  const canSend = phoneTrim.length > 0

  const mut = useMutation({
    mutationFn: async (): Promise<SendSmsResult> => {
      return sendSmsToLead({ phone: phoneTrim, message, leadId, source: 'manual' })
    },
    onSuccess: (r) => {
      setResult(r)
      if (r.ok) {
        void qc.invalidateQueries({ queryKey: ['sms-outbox-count'] })
        void qc.invalidateQueries({ queryKey: ['sms-outbox-list'] })
      }
    },
  })

  const pending = mut.isPending
  const isOk = result?.ok === true
  const segments = countSmsSegments(message)

  return (
    <Modal
      title={t('leads.sms.statusTitle')}
      onClose={onClose}
      footer={
        isOk ? (
          <Button variant="primary" onClick={onClose}>
            {t('common.done')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={pending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setResult(null)
                mut.mutate()
              }}
              disabled={pending || !canSend}
            >
              {pending ? t('leads.sms.sending') : t('leads.sms.send')}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('leads.sms.statusDesc')}
        </p>

        <Input
          label={t('leads.sms.phone')}
          required
          value={phone}
          onChange={(e) => setPhone(formatPhoneInput(phone, e.target.value))}
          disabled={pending || isOk}
          hint={t('leads.sms.phoneHint')}
          placeholder="+7 999 123 45 67"
          inputMode="tel"
        />

        <div>
          <div className="stryzh-input-label !inline-flex items-center gap-3 leading-none">
            <span>{t('leads.sms.messageText')}</span>
          </div>
          <div className="mt-1 rounded border border-[color:var(--stryzh-color-border)] bg-[color:var(--stryzh-color-surface-elevated)] p-3 text-sm">
            <code className="break-all font-mono">{message}</code>
          </div>
          <div className="mt-1 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
            {t('leads.sms.meta', { chars: message.length, segments })}
            {segments > 1 ? t('leads.sms.tariff', { n: segments }) : ''}
          </div>
        </div>

        {result ? <ResultBlock result={result} /> : null}
      </div>
    </Modal>
  )
}

function ResultBlock({ result }: { result: SendSmsResult }) {
  const { t } = useTranslation()
  if (result.ok) {
    return (
      <div className="rounded border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200">
        <div className="font-medium">{t('leads.sms.queued')}</div>
        <div className="mt-1 break-all">
          {result.description}
          {result.attemptedHosts.length > 1 ? (
            <>{t('leads.sms.fallbackVia', { hosts: result.attemptedHosts.join(' → ') })}</>
          ) : null}
        </div>
      </div>
    )
  }
  return (
    <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
      <div className="font-medium">
        {result.code !== null ? t('leads.sms.errCode', { code: result.code }) : t('leads.sms.err')}
      </div>
      <div className="mt-1 break-all">{result.description}</div>
      {result.reason ? <div className="mt-1">{t('leads.sms.reason', { reason: result.reason })}</div> : null}
      {result.attemptedHosts && result.attemptedHosts.length > 0 ? (
        <div className="mt-1">{t('leads.sms.hosts', { hosts: result.attemptedHosts.join(', ') })}</div>
      ) : null}
    </div>
  )
}

function countSmsSegments(text: string): number {
  if (text.length === 0) return 0
  const isUcs2 = /[^\x00-\x7F]/.test(text)
  if (isUcs2) {
    return text.length <= 70 ? 1 : Math.ceil(text.length / 67)
  }
  return text.length <= 160 ? 1 : Math.ceil(text.length / 153)
}
